import { useEffect, useState } from "react";
import { useToast } from "../components/toast";
import { useLoader } from "../context/loaderContext";
import api from "../components/customAxios/Axios";
import { useNavigate } from "react-router-dom";
import AssessmentForm from "@/components/Application/AssessmentForm";
import SwitchTab from "@/components/Application/SwitchTab";
import AddJobDetails from "@/components/Application/AddJobDetails";
import useBreadCrumb from "@/context/BreadCrumbContext";
import { useJobDetails } from "@/context/JobDetailsContext";
import JobDescriptionDoc from "@/components/Application/JobDescriptionDoc";
import JDOptions from "@/components/Application/JDOption";
import PreLoadedJobExamplesModal from "@/components/Application/PreLoadedJobExamplesModal";

interface AssessmentSection {
	checkSection: boolean;
	numQuestions: string;
	questionLevel: string;
	topic: string;
	codingLanguage: string;
}

interface AssessmentConfig {
	cutOfScore: string;
	validity_days: string;
	behavioralAssessment: AssessmentSection;
	technicalAssessment: AssessmentSection;
	codingAssessment: AssessmentSection;
}



function Application() {
	const { setLoading } = useLoader();
	const toast = useToast();
	const navigate = useNavigate();
	const { setBreadCrumb } = useBreadCrumb();
	const { jobDetails, setJobDetails, primaryRequirements, setPrimaryRequirements, secondaryRequirements, setSecondaryRequirements } = useJobDetails();
	const [activeTab, setActiveTab] = useState<"addJD" | "assessmentConfig">("addJD");
	const [file, setFile] = useState<any | null>(null);
	const [isManual, setIsManual] = useState<boolean | null>(null);
	const [assessmentConfig, setAssessmentConfig] = useState<AssessmentConfig>({
		cutOfScore: "75",
		validity_days: "2",
		behavioralAssessment: {
			checkSection: true,
			numQuestions: "4",
			questionLevel: "Medium",
			topic: "",
			codingLanguage: ""
		},
		technicalAssessment: {
			checkSection: false,
			numQuestions: "3",
			questionLevel: "Hard",
			topic: "",
			codingLanguage: ""
		},
		codingAssessment: {
			checkSection: false,
			numQuestions: "2",
			questionLevel: "Easy",
			topic: "",
			codingLanguage: ""
		}
	});
	const handleTabChange = (tab: "addJD" | "assessmentConfig") => {
		setActiveTab(tab);
	};

	const validateAssessment = (checkAssessment: boolean): boolean => {
		if (!checkAssessment) return true
		if (parseFloat(assessmentConfig.cutOfScore) < 1 || !(assessmentConfig.cutOfScore)) {
			toast.error({
				type: "background",
				duration: 5000,
				status: "error",
				title: "Invalid Score",
				description: "Minimum qualification score must be at least 1%. Please enter a value between 1 and 100.",
				open: true,
			});
			return false
		}
		if (parseFloat(assessmentConfig.validity_days) < 1 || !assessmentConfig.validity_days) {
			toast.error({
				type: "background",
				duration: 5000,
				status: "error",
				title: "Invalid Validity Period",
				description: "Assessment link validity must be at least 1 day. Please enter a valid number of days.",
				open: true,
			});
			return false
		}
		const requiredFieldsBySection: Record<keyof AssessmentConfig, string[]> = {
			cutOfScore: [],
			validity_days: [],
			behavioralAssessment: ["numQuestions", "questionLevel"],
			technicalAssessment: ["numQuestions", "questionLevel"],
			codingAssessment: ["numQuestions", "questionLevel", "codingLanguage"],
		};

		for (const section in requiredFieldsBySection) {
			const typedSection = section as keyof AssessmentConfig;
			const sectionConfig = assessmentConfig[typedSection] as AssessmentSection;
			if (sectionConfig?.checkSection) {
				for (const field of requiredFieldsBySection[typedSection]) {
					if (!sectionConfig[field as keyof AssessmentSection]) {
						toast.error({
							type: "background",
							duration: 5000,
							status: "error",
							title: "Incomplete Assessment",
							description: `Please select "${field === "numQuestions" ? "Number of Questions" : field === "questionLevel" ? "Difficulty Level" : "Programming Language"}" in ${section.split("Assessment")[0][0].toUpperCase() + section.split("Assessment")[0].slice(1)} Assessment.`,
							open: true,
						});
						return false;
					}
				}
			}
		}

		return true;
	};

	const handleUploadError = (message: string) => {
		toast.error({
			type: "background",
			duration: 5000,
			status: "error",
			title: "Upload Error",
			description: message,
			open: true,
		});
		setLoading(false);
	};

	const handleReset = () => {
		setActiveTab("addJD")
		setJobDetails({
			title: "",
			company: "",
			location: "",
			description: "",
			salary: "",
			currency: "USD",
			type: "",
			experience: null,
			noticePeriod: ""
		})
		setPrimaryRequirements([])
		setSecondaryRequirements([])
		setFile(null);
		setIsManual(null)
		setAssessmentConfig({
			cutOfScore: "75",
			validity_days: "2",
			behavioralAssessment: {
				checkSection: true,
				numQuestions: "4",
				questionLevel: "Medium",
				topic: "",
				codingLanguage: ""
			},
			technicalAssessment: {
				checkSection: false,
				numQuestions: "3",
				questionLevel: "Hard",
				topic: "",
				codingLanguage: ""
			},
			codingAssessment: {
				checkSection: false,
				numQuestions: "2",
				questionLevel: "Easy",
				topic: "",
				codingLanguage: ""
			}
		});
	};


	const getAssessmentConfigPayload = () => {
		return {
			cutOfScore: assessmentConfig.cutOfScore,
			validity_days: assessmentConfig.validity_days,
			behavioralAssessment: {
				numQuestions: assessmentConfig.behavioralAssessment.checkSection ? assessmentConfig.behavioralAssessment.numQuestions : 0,
				questionLevel: assessmentConfig.behavioralAssessment.questionLevel,

			},
			technicalAssessment: {

				numQuestions: assessmentConfig.technicalAssessment.checkSection ? assessmentConfig.technicalAssessment.numQuestions : 0,
				questionLevel: assessmentConfig.technicalAssessment.questionLevel,
				topic: assessmentConfig.technicalAssessment.topic,

			},
			codingAssessment: {
				numQuestions: assessmentConfig.codingAssessment.checkSection ? assessmentConfig.codingAssessment.numQuestions : 0,
				questionLevel: assessmentConfig.codingAssessment.questionLevel,
				topic: assessmentConfig.codingAssessment.topic,
				codingLanguage: assessmentConfig.codingAssessment.codingLanguage
			}
		}
	}

	const uploadJD = async (checkAssessment: boolean) => {
		if (!jobDetails?.title) {
			handleUploadError("Please enter the Job Role");
			return;
		}
		const wordCount = (jobDetails?.description?.match(/\S+/g) || []).length;
		if (wordCount < 50 || wordCount > 10000) {
			handleUploadError(
				"JD should be between 50 and 10000 words. Please try again."
			);
			return;
		}

		if (!validateAssessment(checkAssessment)) return;

		setLoading(true);
		const assessmentConfigPayload = getAssessmentConfigPayload()
		try {
			const response = await api.post("/manual_upload_job", {
				role: jobDetails?.title,
				jd: jobDetails?.description,
				primarySkills: primaryRequirements,
				secondarySkills: secondaryRequirements,
				salaryRange: jobDetails.currency + " " + jobDetails.salary + " " + jobDetails.salaryPer,
				yearsOfExperience: jobDetails.experience,
				location: jobDetails.location,
				noticePeriod: jobDetails.noticePeriod,
				workLocationType: jobDetails.type,
				assessmentConfig: assessmentConfigPayload,
				generateQuestionsFlag: checkAssessment,
			});
			const { message } = response.data;

			if (message === "Job uploaded successfully.") {
				navigate("/jobList");
				toast.success({
					type: "background",
					duration: 3000,
					status: "Success",
					title: "Success",
					description: "Job JD added.",
					open: true,
				});
				handleReset()
			} else {
				handleUploadError(message);
			}
		} catch (error: any) {
			handleUploadError(
				error.response?.data?.erro || error.response?.data?.message || "An unknown error occurred."
			);
		} finally {
			setLoading(false)
		}
	};

	const uploadDoc = async (checkAssessment: boolean) => {
		if (!jobDetails?.title) {
			handleUploadError("Please enter the Job Role");
			return;
		}

		if (
			!file ||
			!jobDetails?.title ||
			!validateAssessment(checkAssessment)
		)
			return;

		const newResume = new FormData();
		const assessmentConfig = getAssessmentConfigPayload()
		newResume.append("role", jobDetails?.title || "");
		newResume.append("jd_file", file);
		newResume.append("assessmentConfig", JSON.stringify(assessmentConfig));
		newResume.append("generateQuestionsFlag", checkAssessment.toString());
		newResume.append("workLocationType", jobDetails.type || "");
		newResume.append("location", jobDetails.location || "");
		newResume.append("noticePeriod", jobDetails.noticePeriod || "");
		newResume.append("yearsOfExperience", jobDetails.experience || "");
		newResume.append("salaryRange", jobDetails.currency + " " + jobDetails.salary + " " + jobDetails.salaryPer || "");
		newResume.append("primarySkills", primaryRequirements?.toString() || "");
		newResume.append("secondarySkills", secondaryRequirements?.toString() || "");

		setLoading(true);
		try {
			const response = await api.post("/file_upload_jd", newResume);
			if (response.status === 200) {
				navigate("/jobList");
				handleReset()
			} else {
				handleUploadError("Could not upload JD. Please try again.");
			}
		} catch {
			handleUploadError("Could not upload JD. Please try again.");
		} finally {
			setLoading(false)
		}
	};

	useEffect(() => {
		setBreadCrumb({
			mainLabel: {
				label: "Job",
				link: "/joblist",
			},
			currentLabel: "Create a Job",
		});
	}, []);



	return (
		<>
			{/* <Header></Header> */}

			<main className="  bg-white md:p-4  rounded-xl w-full min-h-[85vh] ">
				<div className="max-w-7xl ">
					<h2 className="text-[20px]  text-gray-800 mb-2  font-bold">
						Create New Job
					</h2>
					{isManual === null ? <p className=" mb-4 font-subheading font-normal text-gray-600 text-[15px]"> Upload your own JD or get started with AI to generate a tailored job description for your role.</p> :
						isManual ? <p className=" mb-4 font-subheading font-normal text-gray-600 text-[15px]">
							Upload your job description file (PDF format)
						</p> : <p className=" mb-4 font-subheading font-normal text-gray-600 text-[15px]">
							Enter your job description or select one from the example option
							below.
						</p>
					}

					{isManual === null ? <JDOptions setIsManual={setIsManual} />
						: <>
							<div className="flex justify-between items-center flex-wrap">
								<SwitchTab
									file={file}
									handleTabChange={handleTabChange}
									activeTab={activeTab}
								/>
								{activeTab === "assessmentConfig" ? (
									<div className="flex justify-end mb-1">
										<button
											onClick={() =>
												isManual ? uploadDoc(false) : uploadJD(false)
											}
											className="text-sm mt-3 md:mt-0 md:mx-0 text-[#438437] bg-[#BDE4C3] font-medium hover:scale-110 transition focus:outline-none p-2 rounded-md"
										>
											Skip Assessment
										</button>
									</div>
								) : !isManual ? (
									<div className="flex justify-end mb-1 mr-6">
										<PreLoadedJobExamplesModal />
									</div>
								) : null}
							</div>
							{/* Tab Content */}
							<div className=" font-body">
								{activeTab === "addJD" && (
									<>

										{/* Job Description */}
										<div>
											{isManual ? (
												<div className="max-w-3xl">

													<JobDescriptionDoc file={file} setFile={setFile} />
												</div>
											) : (
												<AddJobDetails

												/>
											)}
										</div>


									</>
								)}
								{activeTab === "assessmentConfig" && (
									<div className=" mt-4 ">
										{/* Your Assessment Config form here */}
										<AssessmentForm
											assessmentConfig={assessmentConfig}
											setAssessmentConfig={setAssessmentConfig}
										/>
									</div>
								)}
								<div className="flex flex-wrap justify-center  gap-4 my-5 max-w-3xl">
									{activeTab === "assessmentConfig" ? (
										<>

											<button
												type="submit"
												onClick={() => {
													isManual ? uploadDoc(true) : uploadJD(true);
												}}
												className="text-sm text-[#438437] bg-[#BDE4C3] font-medium hover:scale-110 transition focus:outline-none   px-3 py-2 rounded-md"
											>
												Submit
											</button>
										</>
									) : (
										<button
											onClick={() => handleTabChange("assessmentConfig")}
											className={`text-sm  font-medium focus:outline-none  px-3 py-2 rounded-md   ${!(jobDetails?.title !== "" && (file !== null || jobDetails?.description !== ""))
												? "text-[#ffff] bg-[#BDE4C3] cursor-not-allowed "
												: "text-[#438437] bg-[#BDE4C3] hover:scale-110 transition "
												}`}
											disabled={
												!(jobDetails?.title !== "" && (file !== null || jobDetails?.description !== ""))
											}
										>
											Next
										</button>
									)}
									<button
										type="reset"
										onClick={handleReset}
										className="text-sm text-[#438437] border-1 border-[#BDE4C3] font-medium hover:scale-110 transition focus:outline-none   px-3 py-2 rounded-md"
									>
										Cancel
									</button>
								</div>
							</div> </>}
				</div>
			</main>
		</>
	);
}

export default Application;
